<template>
  <a-row :gutter="[16, 16]">
    <a-col span="24">
      <Filters
        ref="filtersComponent"
        :show-cycle-input="false"
        :show-duration-input="true"
        :show-steps-filter="false"
        :is-no-data-available="isNoDataAvailable"
        :is-analytics="true"
        @change="handleGetData"
      />
    </a-col>
    <a-col
      v-if="!isNoDataAvailable && !showCompareAnalytics"
      span="24"
      class="text-right"
    >
      <a-space>
        <a-button
          v-if="isAoSmithOrg"
          type="primary"
          :disabled="isFetchingCycles"
          :loading="isDownloadingModelReport"
          @click="downloadModelPerformanceReport"
        >
          Download Model Performance Report
          <template #icon>
            <download-outlined />
          </template>
        </a-button>

        <a-button
          v-if="role == roles.support_user"
          type="primary"
          :disabled="isFetchingCycles"
          @click="downloadSheet"
        >
          Export Process Sheet
          <template #icon>
            <export-outlined />
          </template>
        </a-button>

        <a-button
          type="primary"
          :disabled="isFetchingCycles"
          :loading="isExportingCSV"
          @click="exportCyclesCSV"
        >
          Export cycles CSV
          <template #icon>
            <export-outlined />
          </template>
        </a-button>

        <a-button
          type="primary"
          class="ml-auto"
          :disabled="isFetchingCycles"
          @click="$refs.filtersComponent.redirectToPage('Trace')"
        >
          Trace
          <template #icon>
            <i class="bi bi-box-arrow-up-right mr-2" />
          </template>
        </a-button>
      </a-space>
    </a-col>
    <a-col span="24">
      <a-divider orientation="left" class="mt-2">
        Operation Analytics Dashboard
      </a-divider>

      <div class="d-flex">
        <span
          v-if="isDataNotFound && !isFetchingCycles"
          class="text-center mb-3"
          :class="{
            'w-50': showCompareAnalytics,
            'w-100': !showCompareAnalytics,
          }"
        >
          <a-typography :level="4"> No Data Available </a-typography>
        </span>
        <span
          v-if="
            isDataForComparisonNotFound && !isFetchingAnalyticsForComparison
          "
          class="text-center mb-3 w-50 ml-auto"
        >
          <a-typography :level="4"> No Data Available </a-typography>
        </span>
      </div>
    </a-col>

    <!-- Cards -->
    <a-col
      v-for="(card, index) in cards"
      :key="index"
      :xs="card.colXS"
      :sm="card.colSM"
      :md="card.colMD"
      :lg="card.colLG"
      :xl="card.colXL"
    >
      <CardItem :card="card" :card-index="index" />
    </a-col>

    <!-- Charts -->
    <a-col
      v-for="(chart, index) in charts"
      :key="index"
      :xs="chart.colXS"
      :sm="chart.colSM"
      :md="chart.colMD"
      :lg="chart.colLG"
      :xl="chart.colXL"
    >
      <ChartItem
        v-if="chart.visible"
        :options="chart"
        :total-cycles="
          cards.find((card) => card.key == 'number_of_cycles').value
        "
        :chart-index="index"
      />
    </a-col>
  </a-row>
</template>

<script>
import { DownloadOutlined, ExportOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import dateHelper from 'src/components/shared/Helpers/dateHelper';
import downloadBlob from 'src/components/shared/Helpers/downloadBlob';
import parseTaskList from 'src/components/shared/Helpers/parseTaskList';
import readBlobErrorDetail from 'src/components/shared/Helpers/readBlobErrorDetail';
import {
  cardIndicesForFirstDateRange,
  cardIndicesForFirstDateRangeWithComparison,
  cardIndicesForSecondDateRange,
  cards,
  cardsKeys,
  charts,
  chartsIndicesForFirstDateRange,
  chartsIndicesForFirstDateRangeWithComparison,
  chartsIndicesForSecondDateRange,
  chartsNames,
  screenSizes,
} from 'src/config/charts-config.js';
import { dateTimeFormat } from 'src/config/date-format-config';
import { ORGANIZATIONS } from 'src/config/organizations-config.js';
import { roles } from 'src/config/roles-config.js';
import { GOOD_CYCLE_COLOR, BAD_CYCLE_COLOR } from 'src/config/charts-config.js';
import traceMixin from 'src/mixins/analyticsTrace';
import spaceMixin from 'src/mixins/handleSpace';
import CycleService from 'src/services/cycles.js';
import TelemetryService from 'src/services/telemetry';
import { getSortedDevice } from 'src/utils/device';
import {
  deepClone,
  getSortedTask,
  getTaskNameAndProcess,
} from 'src/utils/task';
import { mapActions, mapGetters } from 'vuex';
import {
  createCustomerInfoTable,
  createCustomerNoteTable,
  createCustomerProcessTable,
  createGeneralNotesTable,
  createModelPerformanceTable,
  createPeriodicAncillary,
  createProcessDescriptionTable,
} from 'src/utils/customerProcessTables';
import Filters from 'src/components/shared/Components/AdvanceFilters';
import CardItem from 'src/components/shared/Components/CardItem.vue';
import jsonToCsvDownload from 'src/components/shared/Helpers/jsonToCsvDownload';
import ChartItem from '../Charts/ChartItem.vue';
export default {
  components: {
    ChartItem,
    CardItem,
    Filters,
    ExportOutlined,
    DownloadOutlined,
  },
  mixins: [traceMixin, spaceMixin],
  inject: ['toast'],
  provide() {
    return {
      redirectToTrace: this.handleRedirectWithCycleDuration,
      redirectMissedToTrace: this.handleRedirectWithMissedSteps,
      redirectInterventionsToTrace: this.handleRedirectWithInterventions,
      playCycle: this.startPlayingCycle,
    };
  },
  setup() {
    return { roles };
  },

  data() {
    return {
      stepLabelsEnglish: [],
      chartList: deepClone(charts),
      cards: deepClone(cards),
      analytics_data: {},
      analyticsDataForComparison: {},
      isDataNotFound: false,
      isDataForComparisonNotFound: false,
      filterValues: null,
      filterValuesForComparison: null,
      videoList: [],
      cycle_type: 'all_cycles',
      pagination: {
        pageSize: 6,
        currentPage: 1,
        options: [6, 10, 20, 50],
      },
      filteredVideoList: [],
      cycleWiseMissedSteps: null,
      jsonToCsvDownload,
      showVideoModal: false,
      videoToPlay: null,
      isFetchingCycleDetails: false,
      isFetchingVideoUrl: false,
      videosToPlay: {},
      stepsList: [],
      initialRow: 14,
      dateHelper,
      isExportingCSV: false,
      isDownloadingModelReport: false,
      reviewStats: null,
    };
  },

  computed: {
    ...mapGetters([
      'organization',
      'devices',
      'stepAvgTime',
      'indexToStepsMapping',
      'deviceSerialNumToDisplayNameMap',
      'stepTime',
      'avgCycleTime',
      'isFetchingCycles',
      'selectedTask',
      'taskDetails',
      'taskName',
      'showCompareAnalytics',
      'isFetchingAnalyticsForComparison',
      'dateRangeForComparison',
      'allTasks',
      'deviceDisplayNameToSerialNumMap',
      'role',
      'translationDetails', // need to change missed_step.label from this.
      'taskProcesses',
      'selectedLang',
      'stepsTranslationMapping',
    ]),

    charts() {
      const hide = [];
      return this.chartList.filter((chart) => !hide.includes(chart.title));
    },

    isNoDataAvailable() {
      return Object.keys(this.analytics_data).length === 0;
    },

    isAoSmithOrg() {
      return this.organization?.toLowerCase() === ORGANIZATIONS.AO_SMITH;
    },

    isReviewedPctBelowThreshold() {
      const pct = this.reviewStats?.reviewed_pct;
      return typeof pct === 'number' && pct < 15;
    },

    taskOptions() {
      return getSortedTask(this.allTasks);
    },

    deviceOptions() {
      return getSortedDevice(this.devices);
    },
  },
  watch: {
    stepsTranslationMapping: {
      handler(value) {
        this.changeStepLanguage(value);
      },
      deep: true,
    },
    selectedLang(value) {
      this.setStepTranslations(value);
    },
    taskDetails(value) {
      this.stepsList = parseTaskList(value);
    },

    showCompareAnalytics(value) {
      if (value) this.handleCompareWithDifferentTimeRange();
      else this.closeCompareWithDifferentTimeRange();
    },
  },
  mounted() {
    this.stepsList = parseTaskList(this.taskDetails);
  },
  methods: {
    ...mapActions([
      'setSelectedLanguage',
      'setIsFetchingCycles',
      'setShowCompareAnalytics',
      'setIsFetchingAnalyticsForComparison',
      'fetchTaskDetails', // to get translation
      'setTaskNameAndProcess',
      'setStepTranslations',
    ]),

    determinePercentage(value) {
      let totalCycles = this.cards.find(
        (card) => card.key == 'number_of_cycles'
      ).value;
      let accuracy = totalCycles - value;
      return Math.floor(((accuracy / totalCycles) * 100).toFixed(2)) + '%';
    },

    downloadSheet() {
      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet 1', {
        properties: { tabColor: { argb: 'FF00FF00' } },
      });

      let { processes } = getTaskNameAndProcess(this.taskDetails);
      let chartData = this.charts.find(
        (data) => data.title == chartsNames.MissedSteps
      ).data;

      let accuracyPercent = chartData.map((data) =>
        this.determinePercentage(data)
      );

      let subSteps = [];
      processes[0].steps.forEach((data) => {
        subSteps.push(...data.substeps);
      });

      let numberOfCycles = this.cards.find(
        (card) => card.key == 'number_of_cycles'
      ).value;

      let dateOfReview = `${this.filterValues.datetime_start.split(' ')[0]} - ${
        this.filterValues.datetime_end.split(' ')[0]
      }`;

      // customer info Table
      // start
      createCustomerInfoTable(
        worksheet,
        this.organization,
        JSON.parse(this.filterValues.devices).join(', ')
      );
      // end
      // Process Description
      // start
      createProcessDescriptionTable(worksheet);
      // end
      // Customer Notes Table
      createCustomerNoteTable(worksheet);
      // end
      // start main table
      createCustomerProcessTable(
        worksheet,
        processes,
        accuracyPercent,
        numberOfCycles,
        dateOfReview,
        this.initialRow
      );
      // end main table
      // Define headers and their widths
      // model performance table
      createModelPerformanceTable(worksheet);
      // model Periodic Ancillary Activities table
      let startingRow = this.initialRow + subSteps.length + 1;
      createPeriodicAncillary(worksheet, startingRow);
      // end
      // General Notes table
      // start
      createGeneralNotesTable(worksheet, startingRow);
      // end
      // reset initial raw
      this.initialRow = 14;
      // Write the workbook to a buffer and save as an Excel file
      workbook.xlsx.writeBuffer().then((buffer) => {
        this.saveAsExcel(buffer, 'Stats.xlsx');
      });
    },

    saveAsExcel(buffer, fileName) {
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    },

    getDateRange(dateRange) {
      const firstDate = `${dayjs(dateRange[0]).format(dateTimeFormat)}`;
      const secondDate = `${`${dayjs(dateRange[1]).format(dateTimeFormat)}`}`;
      return `${firstDate} → ${secondDate}`;
    },

    handleCompareWithDifferentTimeRange() {
      this.cards = this.cards.flatMap((card, index) => [card, cards[index]]);
      this.chartList = this.chartList.flatMap((chart, index) => {
        const newChart = deepClone(charts[index]);
        screenSizes.forEach((screenSize) => {
          chart[screenSize] = newChart[screenSize] = 12;
        });
        return [chart, newChart];
      });
    },

    closeCompareWithDifferentTimeRange() {
      this.isDataForComparisonNotFound = false;

      this.cards = deepClone(cards);
      this.chartList = deepClone(charts);
      this.updateCards();
      this.updateCharts();
    },

    handleRedirectWithCycleDuration({
      bin,
      binSize,
      isFilteredOutliers,
      chartIndex,
    }) {
      const { minCycleTime, maxCycleTime } = this.getMinMaxCycleTime(
        bin,
        binSize,
        isFilteredOutliers,
        chartIndex
      );
      const query = {
        start_time: Math.floor(minCycleTime),
        end_time: Math.ceil(maxCycleTime),
      };
      this.$refs.filtersComponent.redirectToPage('Trace', query, chartIndex);
    },

    handleRedirectWithMissedSteps({ missedSteps, chartIndex }) {
      const query = {
        missed_steps: JSON.stringify(missedSteps),
      };
      this.$refs.filtersComponent.redirectToPage('Trace', query, chartIndex);
    },

    handleRedirectWithInterventions({ interventions, chartIndex }) {
      const query = {
        interventions: JSON.stringify(interventions),
      };
      this.$refs.filtersComponent.redirectToPage('Trace', query, chartIndex);
    },

    async handleGetData(filters) {
      if (filters.isComparing) this.getAnalyticsForComparison(filters);
      else this.getAnalytics(filters);
      await this.fetchTaskDetails(this.selectedTask);
      setTimeout(() => {
        this.setTaskNameAndProcess(
          this.selectedLang !== 'English'
            ? this.translationDetails
            : this.taskDetails
        );
      }, 1000);
    },

    changeStepLanguage(taskProcesses) {
      this.chartList = this.chartList.map((data) => {
        if (
          data.title === chartsNames.MissedSteps ||
          data.title === chartsNames.StepAverageTime
        ) {
          if (
            this.selectedLang !== 'English' &&
            !this.stepLabelsEnglish.length
          ) {
            this.stepLabelsEnglish = [...data.labels];
          }
          return {
            ...data,
            labels:
              this.selectedLang !== 'English'
                ? data.labels.map((label) => taskProcesses[label])
                : this.stepLabelsEnglish,
          };
        }
        return { ...data };
      });
    },

    async getAnalytics(filters) {
      this.cycleWiseMissedSteps = null;
      this.reviewStats = null;
      this.setIsFetchingCycles(true);
      const [error, data] = await TelemetryService.fetchCyclesDetailsV2(
        false,
        filters
      );

      this.filterValues = filters;
      this.setIsFetchingCycles(false);
      if ((data?.body && data?.body == 'No result found') || error) {
        this.clearCharts();
        this.clearCards();
        this.analytics_data = {};
        this.isDataNotFound = true;
        return;
      }
      this.isDataNotFound = false;

      this.analytics_data = data;
      this.updateCards();
      this.updateCharts();

      if (this.isAoSmithOrg) this.fetchReviewStats(filters);
    },

    async fetchReviewStats(filters) {
      const [error, data] = await TelemetryService.fetchTraceReviewStats(
        false,
        filters
      );
      if (error) {
        this.reviewStats = null;
        return;
      }
      this.reviewStats = data;
    },

    async getAnalyticsForComparison(filters) {
      this.setIsFetchingAnalyticsForComparison(true);
      const [error, data] = await TelemetryService.fetchCyclesDetailsV2(
        false,
        filters
      );

      this.filterValuesForComparison = filters;

      if ((data?.body && data?.body == 'No result found') || error) {
        this.clearCards(true);
        this.clearCharts(false);
        this.analyticsDataForComparison = {};
        this.isDataForComparisonNotFound = true;
        this.setIsFetchingAnalyticsForComparison(false);
        return;
      }
      this.isDataForComparisonNotFound = false;

      this.analyticsDataForComparison = data;
      this.updateCards(true);
      this.updateCharts(true);
      this.setIsFetchingAnalyticsForComparison(false);
    },

    fetchCycleWiseMissedSteps() {
      if (this.cycleWiseMissedSteps) return;
      return new Promise(async (resolve) => {
        const queryParams = {
          organization: this.organization,
          devices: this.filterValues.devices,
          task: this.filterValues.task,
          datetime_start: this.filterValues.datetime_start,
          datetime_end: this.filterValues.datetime_end,
          no_page: true,
          ...(this.filterValues.start_time && {
            start_time: this.filterValues.start_time,
          }),
          ...(this.filterValues.end_time && {
            end_time: this.filterValues.end_time,
          }),
          ...(this.filterValues.cycle_type && {
            cycle_type: this.filterValues.cycle_type,
          }),
          ...(this.filterValues.exclude_timeout_cycles && {
            exclude_timeout_cycles: this.filterValues.exclude_timeout_cycles,
          }),
          ...(this.filterValues.model_version && {
            model_version: this.filterValues.model_version,
          }),
          ...(this.filterValues.tags && { tags: this.filterValues.tags }),
          ...(this.filterValues.missed_steps && {
            missed_steps: this.filterValues.missed_steps,
          }),
          ...(this.filterValues.interventions && {
            interventions: this.filterValues.interventions,
          }),
        };
        const [error, data] = await CycleService.fetchCycleWiseMissedStepsV3(
          false,
          queryParams
        );
        if (error) {
          console.log({ error });
          this.cycleWiseMissedSteps = null;
          resolve();
        }

        let formatDataToCycleWiseMissedSteps = [];
        let labels = this.chartList.find((data) => {
          if (data.title === chartsNames.StepAverageTime) {
            return data;
          }
        }).labels;
        data.forEach((cycle) => {
          let object = {};
          object['Cycle Start Time'] = cycle['cycle_identifier'];
          object['Cycle End Time'] = dayjs(cycle.cycle_identifier)
            .add(cycle.total_cycle_time, 'second')
            .format('YYYY-MM-DD HH:mm:ss');
          object['Serial Number'] = cycle['device_serial_number'];
          object['Cycle State'] = cycle.is_good_cycle
            ? 'Good Cycle'
            : 'Bad Cycle';
          object['Missed Step Count'] = cycle.missed_steps_to_display_len;
          object['Cycle Time'] = cycle.total_cycle_time;
          const hasVaNva = cycle.va_nva && Object.keys(cycle.va_nva).length > 0;
          Object.keys(cycle.sub_step_times).forEach((key) => {
            if (labels[key]) {
              const stepName = labels[key];
              const vaData = hasVaNva ? cycle.va_nva[key] || {} : {};

              // Use va_nva total when available so VA + NVA = Total, else fall back to sub_step_times
              object[JSON.stringify(stepName)] =
                vaData.total_time !== undefined
                  ? vaData.total_time
                  : cycle.sub_step_times[key];

              if (hasVaNva) {
                object[JSON.stringify(`${stepName} VA Time (s)`)] =
                  vaData.va_time ?? 0;
                object[JSON.stringify(`${stepName} NVA Time (s)`)] =
                  vaData.nva_time ?? 0;
              }
            }
          });
          formatDataToCycleWiseMissedSteps.push(object);
        });
        console.log(
          'formatDataToCycleWiseMissedSteps',
          formatDataToCycleWiseMissedSteps
        );
        this.cycleWiseMissedSteps = formatDataToCycleWiseMissedSteps;
        resolve();
      });
    },

    async exportCyclesCSV() {
      if (this.analytics_data) {
        this.isExportingCSV = true;

        const toastTimer = setTimeout(async () => {
          this.isExportingCSV = false;
          await this.$nextTick();
          this.toast.info(
            "Your download will start shortly. Please don't close the browser",
            { placement: 'topRight' }
          );
        }, 5000);

        await this.fetchCycleWiseMissedSteps();

        clearTimeout(toastTimer);
        this.isExportingCSV = false;

        const dataToExport = this?.cycleWiseMissedSteps?.map((row) => {
          const processed = {};
          for (const [key, value] of Object.entries(row)) {
            processed[key] =
              value === null || value === undefined || value === '' ? 0 : value;
          }
          return processed;
        });

        const { task, datetime_start, datetime_end } = this.filterValues;
        const start_date = datetime_start.split(' ')[0];
        const end_date = datetime_end.split(' ')[0];

        jsonToCsvDownload(dataToExport, `${task}_${start_date}_${end_date}`);
      } else {
        this.toast.info('No Data Found!');
      }
    },

    async downloadModelPerformanceReport() {
      const { task, datetime_start, datetime_end } = this.filterValues || {};
      if (!task || !datetime_start || !datetime_end) {
        this.toast.info('No Data Found!');
        return;
      }

      const start = datetime_start.split(' ')[0];
      const end = datetime_end.split(' ')[0];

      this.isDownloadingModelReport = true;
      const [error, file] = await TelemetryService.downloadAoSmithReport({
        task,
        start,
        end,
      });
      this.isDownloadingModelReport = false;

      if (error || !file?.blob) {
        const detail = await readBlobErrorDetail(error);
        if (error?.response?.status === 404) {
          this.toast.info(detail || 'No data for the selected task/date range');
        } else {
          this.toast.error(
            detail || 'Failed to generate the model performance report'
          );
        }
        return;
      }

      downloadBlob(
        file.blob,
        file.filename || `model_performance_report_${task}_${start}_${end}.xlsx`
      );
    },

    getMissedSteps(missedStepsIndex) {
      if (!missedStepsIndex) return '';
      return missedStepsIndex
        .split(', ')
        .map((stepIndex) => this.indexToStepsMapping[stepIndex])
        .join('; ')
        .replaceAll(',', '&');
    },

    getMinMaxCycleTime(bin, binSize, isFilteredOutliers, chartIndex) {
      let key = 'analytics_data';
      if (this.showCompareAnalytics) {
        const isChartIndexInSecondDateRange =
          chartsIndicesForSecondDateRange.includes(chartIndex);
        key = isChartIndexInSecondDateRange
          ? 'analyticsDataForComparison'
          : key;
      }

      const maxCycleTime = this[key]['cycle_time_dist']['max_cycle_time'];

      const { bins_of_cycle_times, bins_of_cycle_times_without_outliers } =
        this[key]['cycle_time_dist']['data'][binSize];

      const cyclesTimes = isFilteredOutliers
        ? bins_of_cycle_times_without_outliers
        : bins_of_cycle_times;

      const maxPlotBin = Object.keys(cyclesTimes).at(-1);

      const factor = binSize / 2;
      bin = Number(bin);

      const obj = {
        minCycleTime: bin - factor,
        maxCycleTime: maxPlotBin == bin ? maxCycleTime : bin + factor,
      };
      return obj;
    },

    updateCards(isComparing = false) {
      const cards = [...this.cards];
      const analyticsDataKey = isComparing
        ? 'analyticsDataForComparison'
        : 'analytics_data';

      let cardsIndices = [];

      if (isComparing) {
        cardsIndices = cardIndicesForSecondDateRange;
      } else {
        cardsIndices = this.showCompareAnalytics
          ? cardIndicesForFirstDateRangeWithComparison
          : cardIndicesForFirstDateRange;
      }
      this.updateCardsAccToIndices(cardsIndices, cards, this[analyticsDataKey]);

      this.cards = cards;
    },

    updateCardsAccToIndices(cardsIndices, cards, data) {
      const {
        cycles_with_errors,
        cycles_without_errors,
        timed_out_cycle_count,
      } = data.cycle_stats;
      cardsKeys.forEach((cardKey, index) => {
        cards[cardsIndices[index]] = {
          ...cards[cardsIndices[index]],
          value: data.cycle_stats[cardKey],
        };
        if (index === 0) {
          cards[cardsIndices[index]] = {
            ...cards[cardsIndices[index]],
            goodCycles: cycles_without_errors,
            badCycles: cycles_with_errors,
            timedOutCycles: timed_out_cycle_count,
          };
        }
      });
    },

    clearCards(isComparing = false) {
      const cards = [...this.cards];
      let cardsIndices = [];
      if (isComparing) {
        cardsIndices = cardIndicesForSecondDateRange;
      } else {
        cardsIndices = this.showCompareAnalytics
          ? cardIndicesForFirstDateRangeWithComparison
          : cardIndicesForFirstDateRange;
      }
      cardsKeys.forEach((_, index) => {
        cards[cardsIndices[index]] = {
          ...cards[cardsIndices[index]],
          value: 0,
        };
        if (index === 0) {
          cards[cardsIndices[index]] = {
            ...cards[cardsIndices[index]],
            goodCycles: 0,
            badCycles: 0,
            timedOutCycles: 0,
          };
        }
      });
      this.cards = cards;
    },

    async updateCharts(isComparing = false) {
      const charts = [...this.chartList];
      const chartsHandlers = [
        this.updateStepAvgTimeChart,
        this.updateCycleTimeDistributionChart,
        this.updateMissedStepsChart,
        this.updateNonRelatedActivityChart,
        this.updateTimeDistributionChart,
        this.updateSessionGraph,
        this.updateTimeLineGraphData,
      ];
      const analyticsDataKey = isComparing
        ? 'analyticsDataForComparison'
        : 'analytics_data';
      let chartsIndices = [];
      if (isComparing) {
        chartsIndices = chartsIndicesForSecondDateRange;
      } else {
        chartsIndices = this.showCompareAnalytics
          ? chartsIndicesForFirstDateRangeWithComparison
          : chartsIndicesForFirstDateRange;
      }

      chartsHandlers.forEach((chartHandler, index) => {
        chartHandler(chartsIndices[index], charts, this[analyticsDataKey]);
      });

      this.chartList = charts;
    },

    clearCharts() {
      const chartList = [...this.chartList];
      for (let chart in chartList) {
        chartList[chart].data = '';
        chartList[chart].labels = '';
        chartList[chart].stepsAvgTime = '';
        chartList[chart].stepsStdDev = '';
      }
      this.chartList = chartList;
    },

    updateStepAvgTimeChart(chartIndex, charts, analyticsData) {
      if (!analyticsData['steps_avg_time']) return;
      const { steps_avg_time } = analyticsData;
      const { data, background_times, labels, stepsAvgTime, stepsStdDev } =
        steps_avg_time;
      charts[chartIndex]['background_times'] = background_times;
      charts[chartIndex]['data'] = data;
      const allSubsteps = this.taskProcesses.flatMap((process) =>
        process.steps.flatMap((step) => step.substeps)
      );
      charts[chartIndex]['labels'] = allSubsteps;
      charts[chartIndex]['stepsAvgTime'] = stepsAvgTime;
      charts[chartIndex]['stepsStdDev'] = stepsStdDev;
    },

    updateCycleTimeDistributionChart(chartIndex, charts, analyticsData) {
      if (!analyticsData['cycle_time_dist']) return;
      const { cycle_time_dist } = analyticsData;
      const { data, plotline } = cycle_time_dist;
      charts[chartIndex]['plotLine'] = plotline;
      charts[chartIndex]['data'] = Object.entries(data).reduce(
        (res, [key, value]) => {
          const { bins_of_cycle_times, bins_of_cycle_times_without_outliers } =
            value;
          res[key] = {
            bins_of_cycle_times: this.getFreqAndBins(bins_of_cycle_times),
            bins_of_cycle_times_without_outliers: this.getFreqAndBins(
              bins_of_cycle_times_without_outliers
            ),
          };
          return res;
        },
        {}
      );
    },

    getFreqAndBins(binsOfCycleTimes) {
      const freq = [];
      const bins = [];
      Object.entries(binsOfCycleTimes).forEach(([bin, cyclesCount]) => {
        bins.push(bin);
        freq.push(cyclesCount);
      });
      return {
        freq,
        bins,
      };
    },

    updateMissedStepsChart(chartIndex, charts, analyticsData) {
      if (!analyticsData['missed_steps']) return;
      const { data, labels, interventions } = analyticsData.missed_steps;
      charts[chartIndex]['data'] = data;
      charts[chartIndex]['labels'] = labels;
      charts[chartIndex]['interventions'] = interventions;
    },

    updateNonRelatedActivityChart(chartIndex, charts, analyticsData) {
      if (!analyticsData['nva_time_session']) return;
      const { data, labels } = analyticsData.nva_time_session;
      charts[chartIndex]['data'] = data;
      charts[chartIndex]['labels'] = labels;
    },

    updateTimeDistributionChart(chartIndex, charts, analyticsData) {
      if (!analyticsData['line_chart']) return;
      const { data } = analyticsData.line_chart;
      const labels = [],
        _data = [];

      // Support users see cycles colored by outcome (green good / red bad)
      // instead of the default traced/untraced blue/gray.
      const colorByStatus = this.role === roles.support_user;

      // Sort entries by date to ensure chronological order
      const sortedEntries = Object.entries(data).sort(([keyA], [keyB]) => {
        return dayjs(keyA).valueOf() - dayjs(keyB).valueOf();
      });

      sortedEntries.forEach(([key, value]) => {
        labels.push(key);
        const hasStatus = value.is_good_cycle !== undefined;
        _data.push({
          y: value.cycle_time,
          color:
            colorByStatus && hasStatus
              ? value.is_good_cycle
                ? GOOD_CYCLE_COLOR
                : BAD_CYCLE_COLOR
              : value.is_traced_cycle
              ? '#2caffe'
              : 'lightgray',
          custom: {
            isTraced: value.is_traced_cycle,
            isGoodCycle: value.is_good_cycle,
          },
        });
      });

      charts[chartIndex]['data'] = _data;
      charts[chartIndex]['labels'] = labels;
    },

    updateSessionGraph(chartIndex, charts, analyticsData) {
      if (!analyticsData['cycle_count_by_session']) return;
      const { data, labels } = analyticsData.cycle_count_by_session;
      charts[chartIndex]['data'] = data;
      charts[chartIndex]['labels'] = labels;
    },

    getSessionChartData(temp, labels) {
      temp = deepClone(temp);
      let maxSessionCount = Math.max(
        ...Object.values(temp).map((r) => r.length)
      );
      const chartData = [];
      for (let i = 0; i < maxSessionCount; i++) {
        let verticalBar = {
          type: 'errorbar',
          data: [],
        };
        chartData.push({
          type: 'bar',
          data: labels.map((d) => {
            const y = temp[d]?.shift();
            if (!y) {
              verticalBar.data.push([]);
              return null;
            }
            const { bar, line } = this.getSessionChartPointData(y);
            verticalBar.data.push(line);
            return bar;
          }),
        });
        chartData.push(verticalBar);
      }
      return chartData;
    },

    getSessionChartPointData(y) {
      let totalCycles = null;
      if (this.avgCycleTime) {
        totalCycles = Math.round(y['sessionDur'] / this.avgCycleTime);
      }
      return {
        bar: {
          startTime: y['startTime'],
          endTime: y['endTime'],
          y: y['cycleCount'],
          totalCycles: totalCycles,
          sessionDur: y['sessionDur'],
          color: '#8AC3F6',
        },
        line: totalCycles ? [0, totalCycles] : [],
      };
    },

    getSessionNonRelatedActivityData(temp, labels) {
      temp = deepClone(temp);
      let maxSessionCount = Math.max(
        ...Object.values(temp).map((r) => r.length)
      );
      const chartData = [];
      for (let i = 0; i < maxSessionCount; i++) {
        chartData.push({
          type: 'bar',
          data: labels.map((d) => {
            const y = temp[d]?.shift();
            // const y = temp[d][i];
            if (!y) return null;

            const bar = this.getSessionNonRelatedActivityValue(y);

            return bar;
          }),
        });
      }
      return chartData;
    },

    getSessionNonRelatedActivityValue(y) {
      return {
        startTime: y['startTime'],
        endTime: y['endTime'],
        y: y['avg_background_time'],
        sessionDur: y['sessionDur'],
        color: '#8AC3F6',
      };
    },

    updateTimeLineGraphData(chartIndex, charts, analyticsData) {
      if (!analyticsData['time_line_chart']) return;
      const { data, chart_height, dates } = analyticsData.time_line_chart;

      // Support users see each cycle bar colored by outcome (green good / red bad).
      const colorByStatus = this.role === roles.support_user;

      charts[chartIndex].data = data.map((device) => ({
        ...device,
        name: this.deviceSerialNumToDisplayNameMap[device.name],
        ...(colorByStatus &&
          Array.isArray(device.data) && {
            data: device.data.map((point) => {
              const isGoodCycle = point?.custom?.is_good_cycle;
              if (isGoodCycle === undefined) return point;
              return {
                ...point,
                color: isGoodCycle ? GOOD_CYCLE_COLOR : BAD_CYCLE_COLOR,
              };
            }),
          }),
      }));
      charts[chartIndex].chartHeight = chart_height;
      charts[chartIndex].dates = dates;
    },

    async startPlayingCycle(cycleIdentifier) {
      if (this.isVideoExistsInState(cycleIdentifier)) return;
      const [, data] = await this.getCycleDetails(cycleIdentifier);
      let { href } = this.$router.resolve({
        name: 'Cycle Trace',
        params: {
          traceId: data.id,
        },
        query: {
          taskId: data.Task,
          cycle_id: data.cycle_id,
        },
      });
      window.open(href, '_blank');
    },

    isVideoExistsInState(cycleIdentifier) {
      if (this.videosToPlay[cycleIdentifier]) {
        this.videoToPlay = this.videosToPlay[cycleIdentifier];
        this.isFetchingCycleDetails = false;
        return true;
      }
      return false;
    },

    getCycleDetails(cycleIdentifier) {
      const { device_id } =
        this.analytics_data.line_chart.data[cycleIdentifier];
      return new Promise(async (resolve) => {
        const [error, data] = await CycleService.fetchCycleDetailsV2(
          cycleIdentifier,
          device_id,
          this.selectedTask
        );
        data['missed_steps'] = data?.missed_steps?.join(',') ?? '';
        resolve([error, data]);
      });
    },
  },
};
</script>
